import { supabase } from './supabaseClient';

interface SendEmailParams {
  to: string;
  subject: string;
  template: string;
  data: Record<string, any>;
  campaignId?: string;
  userId?: string;
  organisationId?: string;
  replyTo?: string;
  attachments?: Array<{ filename: string; content: string; type?: string }>;
  fromName?: string; // Custom sender name (default: PCNGuard Fleet)
  fromEmail?: string; // Custom sender email (must be verified in Resend)
}

interface EmailResponse {
  success: boolean;
  emailId?: string;
  error?: string;
}

/**
 * Email Service
 * Handles sending emails via Supabase Edge Function
 */
export const EmailService = {
  async sendEmailViaHttp(params: SendEmailParams): Promise<EmailResponse | null> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseAnonKey) return null;

    let bearer: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) bearer = data.session.access_token;
    } catch {
      // ignore
    }

    if (!bearer) return null;

    try {
      const fnUrl = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/send-email`;
      const fallback = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({
          to: params.to,
          subject: params.subject,
          template: params.template,
          data: params.data,
          campaign_id: params.campaignId,
          user_id: params.userId,
          organisation_id: params.organisationId,
          reply_to: params.replyTo,
          attachments: params.attachments,
          from_name: params.fromName,
          from_email: params.fromEmail,
        }),
      });

      const fallbackJson = await fallback.json().catch(() => null);
      if (fallback.ok && fallbackJson?.success) {
        return {
          success: true,
          emailId: fallbackJson.email_id,
        };
      }
      const details = fallbackJson?.error || fallbackJson?.message || fallback.statusText;
      return {
        success: false,
        error: `Edge function HTTP ${fallback.status}: ${details || 'Request failed'}`,
      };
    } catch (fallbackErr) {
      console.error('Email fallback error:', fallbackErr);
      // Provide more detailed error info
      const errorDetails = fallbackErr instanceof Error
        ? `${fallbackErr.name}: ${fallbackErr.message}`
        : String(fallbackErr);
      console.error('Email fallback URL:', `${supabaseUrl}/functions/v1/send-email`);
      return { success: false, error: `Network error: ${errorDetails}` };
    }
  },

  /**
   * Send an email using a template (with optional retry for transient errors)
   */
  async sendEmail(params: SendEmailParams, retries = 1): Promise<EmailResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: params.to,
          subject: params.subject,
          template: params.template,
          data: params.data,
          campaign_id: params.campaignId,
          user_id: params.userId,
          organisation_id: params.organisationId,
          reply_to: params.replyTo,
          attachments: params.attachments,
          from_name: params.fromName,
          from_email: params.fromEmail,
        },
      });

      if (error) {
        const errorMsg = error.message || JSON.stringify(error);
        const fallbackResult = await this.sendEmailViaHttp(params);
        if (fallbackResult) return fallbackResult;
        // Retry on transient errors (timeout, 503)
        if (retries > 0 && (errorMsg.includes('timeout') || errorMsg.includes('503') || errorMsg.includes('504'))) {
          console.log(`Email send failed, retrying (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.sendEmail(params, retries - 1);
        }
        console.error('Email send error:', error);
        return { success: false, error: errorMsg };
      }

      // Handle edge function returning { success: false, reason/error: ... }
      if (data && !data.success) {
        const reason = data.error || data.reason || 'Unknown error';
        return { success: false, error: reason };
      }

      return {
        success: data?.success ?? false,
        emailId: data?.email_id,
        error: data?.error,
      };
    } catch (err) {
      const errMsg = String(err);
      const fallbackResult = await this.sendEmailViaHttp(params);
      if (fallbackResult) return fallbackResult;
      // Retry on network errors
      if (retries > 0 && (errMsg.includes('fetch') || errMsg.includes('network') || errMsg.includes('timeout'))) {
        console.log(`Email service error, retrying (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.sendEmail(params, retries - 1);
      }
      console.error('Email service error:', err);
      return { success: false, error: errMsg };
    }
  },

  /**
   * Send welcome email to new user
   */
  async sendWelcomeEmail(
    email: string,
    firstName: string,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('welcome_email');

    return this.sendEmail({
      to: email,
      subject: campaign?.subject_line || 'Welcome to PCNGuard Fleet',
      template: 'WelcomeEmail',
      data: {
        firstName,
        dashboardUrl: `${window.location.origin}/dashboard`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  async sendTecSubmissionEmail(params: {
    to: string;
    subject: string;
    data: {
      contactName?: string;
      contactEmail?: string;
      contactPhone?: string;
      pcnNumber?: string;
      vrm?: string;
      forms?: string;
      statement?: string;
    };
    attachments: Array<{ filename: string; content: string; type?: string }>;
    replyTo?: string;
    userId?: string;
    organisationId?: string;
    fromName?: string;
    fromEmail?: string;
  }): Promise<EmailResponse> {
    return this.sendEmail({
      to: params.to,
      subject: params.subject,
      template: 'TecSubmissionEmail',
      data: params.data,
      replyTo: params.replyTo,
      attachments: params.attachments,
      userId: params.userId,
      organisationId: params.organisationId,
      fromName: params.fromName,
      fromEmail: params.fromEmail,
    });
  },

  /**
   * Send quick win email (Day 1 - no tickets uploaded)
   */
  async sendQuickWinEmail(
    email: string,
    firstName: string,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('quick_win_email');

    return this.sendEmail({
      to: email,
      subject: campaign?.subject_line || 'Upload Your First PCN in 30 Seconds',
      template: 'QuickWinEmail',
      data: {
        firstName,
        uploadUrl: `${window.location.origin}/tickets`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  /**
   * Send trial expiry reminder
   */
  async sendTrialExpiryEmail(
    email: string,
    firstName: string,
    hoursLeft: number,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('trial_expiry_48h');

    return this.sendEmail({
      to: email,
      subject:
        campaign?.subject_line || `Last Day: Your Pro Trial Expires in ${hoursLeft} Hours`,
      template: 'TrialExpiryEmail',
      data: {
        firstName,
        hoursLeft,
        billingUrl: `${window.location.origin}/billing`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  /**
   * Send upgrade teaser email (10+ tickets on Starter)
   */
  async sendUpgradeTeaserEmail(
    email: string,
    firstName: string,
    ticketCount: number,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('upgrade_teaser_10_tickets');

    const workflowValue = ticketCount * 20;
    const growthPlan = 179;
    const planShare = workflowValue > 0 ? Math.round((growthPlan / workflowValue) * 100) : 0;

    return this.sendEmail({
      to: email,
      subject: campaign?.subject_line || 'Your PCN workflow may be ready for FleetMode Growth',
      template: 'UpgradeTeaserEmail',
      data: {
        firstName,
        ticketCount,
        starterCost: 'GBP 79/month',
        proCost: 'GBP 179/month',
        savingsAmount: `${planShare}% of estimated admin-fee workflow value`,
        pricingUrl: `${window.location.origin}/pricing`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  /**
   * Send re-engagement email for dormant users
   */
  async sendDormantUserEmail(
    email: string,
    firstName: string,
    daysInactive: number,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('dormant_30_days');

    return this.sendEmail({
      to: email,
      subject: campaign?.subject_line || 'Still Wrestling with Parking Fines Manually?',
      template: 'DormantUserEmail',
      data: {
        firstName,
        daysInactive,
        dashboardUrl: `${window.location.origin}/dashboard`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  /**
   * Send payment failed notification
   */
  async sendPaymentFailedEmail(
    email: string,
    firstName: string,
    userId: string,
    organisationId: string
  ): Promise<EmailResponse> {
    const campaign = await this.getCampaignByName('payment_failed');

    return this.sendEmail({
      to: email,
      subject:
        campaign?.subject_line || 'Payment Failed: Update Your Card to Keep Pro Features',
      template: 'PaymentFailedEmail',
      data: {
        firstName,
        billingUrl: `${window.location.origin}/billing`,
      },
      campaignId: campaign?.id,
      userId,
      organisationId,
    });
  },

  /**
   * Get campaign configuration by name
   */
  async getCampaignByName(name: string) {
    const { data, error } = await supabase
      .from('email_campaigns')
      .select('*')
      .eq('name', name)
      .eq('status', 'active')
      .single();

    if (error) {
      console.error('Failed to fetch campaign:', error);
      return null;
    }

    return data;
  },

  /**
   * Get email logs for a user
   */
  async getUserEmailLogs(userId: string, limit = 50) {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to fetch email logs:', error);
      return [];
    }

    return data;
  },

  /**
   * Update user email preferences
   */
  async updateEmailPreferences(
    userId: string,
    preferences: {
      transactional?: boolean;
      lifecycle?: boolean;
      marketing?: boolean;
      retention?: boolean;
      frequency?: 'daily' | 'weekly' | 'monthly';
    }
  ) {
    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: userId,
        preferences: {
          email_preferences: preferences,
        },
      });

    if (error) {
      console.error('Failed to update email preferences:', error);
      return false;
    }

    return true;
  },

  /**
   * Get user email preferences
   */
  async getEmailPreferences(userId: string) {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .single();

    if (error || !data) {
      // Return default preferences
      return {
        transactional: true,
        lifecycle: true,
        marketing: true,
        retention: true,
        frequency: 'daily' as const,
      };
    }

    return data.preferences?.email_preferences || {
      transactional: true,
      lifecycle: true,
      marketing: true,
      retention: true,
      frequency: 'daily' as const,
    };
  },
};
